import { ErrorHandler } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LatLon } from '../../util/geodesy/latlon';
import { BoundingBox } from '../../util/geodesy/bounding-box';

/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export const MIN_RADIUS = 25;

/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export const MAX_RADIUS = 500;

/**
 * Base HTTP service for the location-based business finders (Flying Clubs today; Flight Schools and
 * Universities later). Mirrors {@link AviationEventsService}: it POSTs to a single endpoint with one of
 * four request-body shapes (point+radius / rectangleLatLong / stateProvince / searchTerm). Subclasses
 * supply the endpoint path (via {@link endpoint}) and a {@link parse} function, and expose
 * domain-named wrappers around the protected `getBy*` methods.
 */
export abstract class BusinessFinderService<T> {

  /**
   * Resource path appended to the AirportsAPI base url (e.g. 'flying-clubs').
   */
  protected abstract readonly endpoint: string;

  /**
   * Parses a single raw API record into the typed result.
   */
  protected abstract parse(data: any): T;

  protected constructor(protected _http: HttpClient, protected _errorHandler: ErrorHandler) {
  }

  /**
   * Returns a sanitized search term with all invalid characters removed.
   */
  static sanitizeSearchTerm(term: string): string {
    return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
  }

  protected get url(): string {
    return `${environment.airportsApiUrl}/${this.endpoint}`;
  }

  /**
   * Performs a radial search within a given distance of a center point.
   * @param latlon {LatLon} Center point of search
   * @param radius {number} Radius in nautical miles
   */
  async getByLatLon(latlon: LatLon, radius: number): Promise<T[]> {
    if (!latlon) {
      throw new Error('latlon is required');
    }
    if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
      throw new Error(`radius is required and must be between ${MIN_RADIUS} and ${MAX_RADIUS} nautical miles.`);
    }
    try {
      const response = await this._http.post<any[]>(this.url, {
        point: {
          latitude: latlon.lat,
          longitude: latlon.lon
        },
        radius: radius
      }).toPromise();
      return response.map(d => this.parse(d));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Performs a bounding box (map area) search.
   * @param bounds {BoundingBox} Bounding box
   */
  async getByBoundingBox(bounds: BoundingBox): Promise<T[]> {
    try {
      const neLat = bounds.ne.lat.toFixed(7);
      const neLon = bounds.ne.lon.toFixed(7);
      const swLat = bounds.sw.lat.toFixed(7);
      const swLon = bounds.sw.lon.toFixed(7);
      const response = await this._http.post<any[]>(this.url, {
        rectangleLatLong: `${neLat},${neLon},${swLat},${swLon}`
      }).toPromise();
      return response.map(d => this.parse(d));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns all results within a given state.
   * @param state {string} State code
   */
  async getByState(state: string): Promise<T[]> {
    if (!state) {
      throw new Error('"state" parameter is required when searching by state');
    }
    try {
      const response = await this._http.post<any[]>(this.url, {
        stateProvince: state
      }).toPromise();
      return response.map(d => this.parse(d));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns results matching a keyword (name or airport ident).
   * @param term {string} Search term
   */
  async getBySearchTerm(term: string): Promise<T[]> {
    term = BusinessFinderService.sanitizeSearchTerm(term);
    if (term.length < 3) {
      throw new Error('At least three alphanumeric characters are necessary to perform a search.');
    }
    try {
      const response = await this._http.post<any[]>(this.url, {
        searchTerm: `%${term}%`
      }).toPromise();
      return response.map(d => this.parse(d));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }
}
